import React from 'react'
import { MessageSquare, Clock, AlertCircle, CheckCircle, Building2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { ticketService, Ticket } from '@/services/ticketService'
import { useWebSocketContext } from '@/context/WebSocketContext'
import { useAuth } from '@/context/AuthContext'
import { hasAdminMenuAccess } from '@/lib/adminPermissions'
import { Badge } from '@/components/ui/badge'

// HTML tag'lerini temizle
const stripHtml = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>?/g, '')     // Tüm HTML tag'lerini temizle (yarım olanlar dahil)
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

interface TicketNotificationsProps {
  isOpen: boolean
  onClose: () => void
}

export const TicketNotifications: React.FC<TicketNotificationsProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canAccessTickets = hasAdminMenuAccess(user, 'tickets')
  const { addMessageHandler, removeMessageHandler } = useWebSocketContext()
  const [unreadTickets, setUnreadTickets] = React.useState<Ticket[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const debounceTimerRef = React.useRef<NodeJS.Timeout>()

  // Ses çal
  const playNotificationSound = React.useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      const playTone = (frequency: number, startTime: number, duration: number) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.value = frequency
        oscillator.type = 'sine'

        gainNode.gain.setValueAtTime(0.5, startTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration)

        oscillator.start(startTime)
        oscillator.stop(startTime + duration)
      }

      const now = audioContext.currentTime
      playTone(800, now, 0.15)        // İlk ton
      playTone(600, now + 0.15, 0.15) // İkinci ton
    } catch (error) {
      console.error('Ses çalınamadı:', error)
    }
  }, [])

  // Okunmamış ticketları getir
  const fetchUnreadTickets = React.useCallback(async () => {
    if (!canAccessTickets) return
    try {
      setIsLoading(true)
      const response = await ticketService.getTickets({ limit: 10 })
      // Sadece okunmamış ticketları filtrele
      const unread = (response.data || []).filter((ticket: Ticket) => ticket.adminUnread)
      setUnreadTickets(unread)
    } catch (error) {
      console.error('Okunmamış ticketlar getirilirken hata:', error)
    } finally {
      setIsLoading(false)
    }
  }, [canAccessTickets])

  // Debounced fetch
  const debouncedFetchUnreadTickets = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchUnreadTickets()
    }, 300)
  }, [fetchUnreadTickets])

  // Component mount olduğunda ve dropdown açıldığında ticketları getir
  React.useEffect(() => {
    if (isOpen) {
      fetchUnreadTickets()
    }
  }, [isOpen, fetchUnreadTickets])

  // WebSocket ile gerçek zamanlı güncellemeler
  React.useEffect(() => {
    const handleMessage = (message: any) => {
      // Yeni ticket veya müşteriden gelen mesajda ses çal (admin'in kendi mesajında çalma)
      if (message.type === 'ticket_new' ||
          (message.type === 'ticket_message' && message.data?.sender?.userType !== 'admin')) {
        playNotificationSound()
        debouncedFetchUnreadTickets()
      } else if (message.type === 'ticket_status' || message.type === 'ticket_read') {
        debouncedFetchUnreadTickets()
      } else if (message.type === 'ticket_message' && message.data?.sender?.userType === 'admin') {
        // Admin kendi mesajını gönderdiğinde sadece listeyi güncelle, ses çalma
        debouncedFetchUnreadTickets()
      }
    }

    addMessageHandler(handleMessage)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      removeMessageHandler(handleMessage)
    }
  }, [addMessageHandler, removeMessageHandler, debouncedFetchUnreadTickets, playNotificationSound])

  // Ticket'a tıklandığında
  const handleTicketClick = (ticketId: string) => {
    navigate(`/tickets/${ticketId}`)
    onClose()
  }

  // Tüm ticketları göster
  const handleViewAll = () => {
    navigate('/tickets')
    onClose()
  }

  // Status icon
  const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-4 h-4 text-green-500" />
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-500" />
      case 'waiting_customer':
        return <Clock className="w-4 h-4 text-yellow-500" />
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-purple-500" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  if (!isOpen) return null

  return (
    <div className="absolute right-0 mt-2 w-96 bg-card rounded-lg shadow-lg border border-border max-h-[500px] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-500" />
            Destek Talepleri
          </h3>
          {unreadTickets.length > 0 && (
            <Badge className="bg-red-500 text-white hover:bg-red-600">
              {unreadTickets.length} yeni
            </Badge>
          )}
        </div>
      </div>

      {/* Ticket List */}
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Yükleniyor...
          </div>
        ) : unreadTickets.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Okunmamış destek talebi yok</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {unreadTickets.map((ticket) => (
              <div
                key={ticket._id}
                onClick={() => handleTicketClick(ticket._id)}
                className="p-3 hover:bg-muted cursor-pointer transition-colors"
              >
                <div className="flex items-start gap-3">
                  <StatusIcon status={ticket.status} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-muted-foreground font-mono">
                        #{ticket.ticketNumber}
                      </span>
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${ticketService.getStatusColor(ticket.status)}`}>
                        {ticketService.getStatusText(ticket.status)}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${ticketService.getPriorityColor(ticket.priority)}`}>
                        {ticketService.getPriorityText(ticket.priority)}
                      </span>
                    </div>
                    <p className="font-medium text-sm text-foreground truncate">
                      {ticket.subject}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                      {stripHtml(ticket.lastMessagePreview || ticket.description)}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                      <Building2 className="w-3 h-3" />
                      <span className="truncate">
                        {ticket.company?.name || 'Bilinmeyen'}
                      </span>
                      <span>•</span>
                      <span>{ticket.createdBy.name}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {unreadTickets.length > 0 && (
        <div className="p-3 border-t border-border">
          <button
            onClick={handleViewAll}
            className="w-full text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors py-2"
          >
            Tüm Destek Taleplerini Görüntüle
          </button>
        </div>
      )}
    </div>
  )
}

// Hook to get unread count for badge
export const useUnreadTicketCount = () => {
  const { user } = useAuth()
  const canAccessTickets = hasAdminMenuAccess(user, 'tickets')
  const { addMessageHandler, removeMessageHandler } = useWebSocketContext()
  const [unreadCount, setUnreadCount] = React.useState(0)
  const debounceTimerRef = React.useRef<NodeJS.Timeout>()

  const fetchUnreadCount = React.useCallback(async () => {
    if (!canAccessTickets) return
    try {
      const response = await ticketService.getTickets({ limit: 100 })
      const unread = (response.data || []).filter((ticket: Ticket) => ticket.adminUnread)
      setUnreadCount(unread.length)
    } catch (error) {
      console.error('Okunmamış ticket sayısı alınamadı:', error)
    }
  }, [canAccessTickets])

  // Debounced fetch
  const debouncedFetchUnreadCount = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchUnreadCount()
    }, 300)
  }, [fetchUnreadCount])

  React.useEffect(() => {
    fetchUnreadCount()
  }, [fetchUnreadCount])

  // Browser tab title'ı güncelle (Intercom tarzı)
  React.useEffect(() => {
    const originalTitle = 'Super Admin Panel'

    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${originalTitle}`
    } else {
      document.title = originalTitle
    }

    // Cleanup
    return () => {
      document.title = originalTitle
    }
  }, [unreadCount])

  // WebSocket ile gerçek zamanlı güncellemeler
  React.useEffect(() => {
    const handleMessage = (message: any) => {
      if (message.type === 'ticket_new') {
        // Yeni ticket - count artır
        setUnreadCount(prev => prev + 1)
      } else if (message.type === 'ticket_read') {
        // Okundu - count azalt
        setUnreadCount(prev => Math.max(0, prev - 1))
      } else if (message.type === 'ticket_message' || message.type === 'ticket_status') {
        // Mesaj veya durum değişikliği - API'den güncel sayıyı al
        // (Ticket zaten okunmamış olabilir, duplicate olmaması için API'den al)
        debouncedFetchUnreadCount()
      }
    }

    addMessageHandler(handleMessage)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      removeMessageHandler(handleMessage)
    }
  }, [addMessageHandler, removeMessageHandler, debouncedFetchUnreadCount])

  return unreadCount
}
